import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useRouter, Link } from '@tanstack/react-router'
import { useForm, useFieldArray, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { TextRow, TextareaField } from '@/components/ui/prntr-fields'
import { FormSkeleton } from '@/components/ui/skeleton-presets'
import { ArrowLeft, MessageSquare, MoreVertical } from '@/components/icons'
import { EmptyState } from '@/components/EmptyState'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ChecklistTemplateItemCard,
  type FieldItem,
} from '@/components/ChecklistTemplateItemCard'
import { ChecklistTemplatePreviewFields } from '@/components/ChecklistTemplatePreviewFields'
import { type StarterTemplate } from '@/lib/starter-checklist-templates'
import {
  useChecklistTemplate,
  useChecklistTemplateCreate,
  useChecklistTemplateArchive,
  useChecklistTemplateDuplicate,
  useChecklistTemplateUpdate,
} from '@/lib/hooks'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

const templateFormSchema = z.object({
  name: z.string().min(1, 'Template title is required'),
  description: z.string().optional(),
  items: z.array(
    z.object({
      id: z.string(),
      type: z.enum(['YesNo', 'Text', 'TextArea', 'Number']),
      label: z.string(),
      isRequired: z.boolean(),
    })
  ),
})

type TemplateFormData = z.infer<typeof templateFormSchema>

type ChecklistTemplateEditorProps =
  | { mode: 'create'; starterDef?: StarterTemplate }
  | { mode: 'edit'; templateId: string }

export function ChecklistTemplateEditor(props: ChecklistTemplateEditorProps) {
  const navigate = useNavigate()
  const router = useRouter()
  const isDesktop = useIsDesktop()

  const isEditMode = props.mode === 'edit'
  const starterDef = props.mode === 'create' ? props.starterDef : undefined
  const isFromStarter = props.mode === 'create' && !!props.starterDef

  const { data: existingTemplate, isLoading } = useChecklistTemplate(
    props.mode === 'edit' ? props.templateId : null
  )

  const { mutate: createTemplate, isPending: isCreating } = useChecklistTemplateCreate()
  const { mutate: updateTemplate, isPending: isUpdating } = useChecklistTemplateUpdate()
  const { mutate: duplicateTemplate } = useChecklistTemplateDuplicate()
  const { mutate: archiveTemplate } = useChecklistTemplateArchive()

  const isSaving = isCreating || isUpdating

  const [isPreview, setIsPreview] = useState(false)
  const [isArranging, setIsArranging] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [nextTempId, setNextTempId] = useState(() =>
    props.mode === 'create' ? starterDef?.items.length ?? 0 : 0
  )

  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateFormSchema),
    defaultValues: starterDef
      ? {
          name: starterDef.name,
          description: starterDef.description,
          items: starterDef.items.map((it, i) => ({
            id: `temp-${i}`,
            type: it.type,
            label: it.label,
            isRequired: it.isRequired,
          })),
        }
      : {
          name: '',
          description: '',
          items: [],
        },
    mode: 'onChange',
  })

  const { fields, prepend, remove, move } = useFieldArray({
    control,
    name: 'items',
  })

  useEffect(() => {
    if (existingTemplate) {
      reset({
        name: existingTemplate.name,
        description: existingTemplate.description ?? '',
        items: existingTemplate.items.map(
          (item: {
            id: string
            type: string
            label: string
            isRequired: boolean
            sortOrder: number
          }) => ({
            id: item.id,
            type: item.type as 'YesNo' | 'Text' | 'TextArea' | 'Number',
            label: item.label,
            isRequired: item.isRequired,
          })
        ),
      })
    }
  }, [existingTemplate, reset])

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const addField = useCallback(() => {
    prepend({
      id: `temp-${nextTempId}`,
      type: 'Text',
      label: '',
      isRequired: false,
    })
    setNextTempId((prev) => prev + 1)
  }, [prepend, nextTempId])

  const updateField = useCallback(
    (id: string, updates: Partial<FieldItem>) => {
      const index = fields.findIndex((f) => f.id === id)
      if (index === -1) return
      const formKeys = ['type', 'label', 'isRequired'] as const
      for (const key of formKeys) {
        if (key in updates) {
          setValue(
            `items.${index}.${key}` as const,
            updates[key] as never,
            { shouldDirty: true, shouldTouch: true }
          )
        }
      }
    },
    [fields, setValue]
  )

  const removeField = useCallback(
    (id: string) => {
      const index = fields.findIndex((f) => f.id === id)
      if (index !== -1) remove(index)
    },
    [fields, remove]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        const oldIndex = fields.findIndex((f) => f.id === active.id)
        const newIndex = fields.findIndex((f) => f.id === over.id)
        move(oldIndex, newIndex)
      }
    },
    [fields, move]
  )

  const handleCancel = () => {
    if (isDirty) {
      setShowDiscardDialog(true)
    } else {
      navigate({ to: '/operations/checklisttemplates' })
    }
  }

  const onSubmit = (data: TemplateFormData) => {
    const onError = (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save template')
    }

    if (props.mode === 'edit') {
      updateTemplate(
        {
          id: props.templateId,
          name: data.name.trim(),
          // '' not undefined — Prisma ignores undefined keys, so clearing the
          // description would silently keep the old value.
          description: data.description?.trim() || '',
          items: data.items.map((f, i) => ({
            id: f.id.startsWith('temp-') ? undefined : f.id,
            label: f.label || `Field ${i + 1}`,
            type: f.type,
            sortOrder: i,
            isRequired: f.isRequired,
          })),
        },
        {
          onSuccess: () => {
            toast.success('Template updated')
            reset(data)
            router.history.back()
          },
          onError,
        }
      )
    } else {
      createTemplate(
        {
          name: data.name.trim(),
          description: data.description?.trim() || undefined,
          items: data.items.map((f, i) => ({
            label: f.label || `Field ${i + 1}`,
            type: f.type,
            sortOrder: i,
            isRequired: f.isRequired,
          })),
        },
        {
          onSuccess: () => {
            toast.success('Template created')
            router.history.back()
          },
          onError,
        }
      )
    }
  }

  const onInvalid = (errs: FieldErrors<TemplateFormData>) => {
    const first =
      errs.name?.message ??
      errs.description?.message ??
      (Array.isArray(errs.items)
        ? errs.items.find(Boolean)?.label?.message ??
          errs.items.find(Boolean)?.type?.message
        : undefined) ??
      'Please fix the highlighted fields'
    toast.error(String(first))
  }

  const handleDuplicate = () => {
    if (props.mode !== 'edit') return
    duplicateTemplate(
      { id: props.templateId },
      {
        onSuccess: (created) => {
          navigate({
            to: '/operations/checklisttemplates/edit/$checklistId',
            params: { checklistId: created.id },
          })
        },
      }
    )
  }

  const handleArchive = () => {
    if (props.mode !== 'edit') return
    archiveTemplate(
      { id: props.templateId },
      {
        onSuccess: () => {
          toast.success('Template archived')
          navigate({ to: '/operations/checklisttemplates' })
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Failed to archive template')
        },
      }
    )
  }

  const watchedName = watch('name')

  const topBarTitle = isPreview
    ? `Preview${watchedName ? ` - ${watchedName}` : ''}`
    : isEditMode
      ? 'Edit Checklist'
      : 'New Checklist'

  const topBarRightIcon = !isPreview ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="icon"
          roundness="round"
          className="size-8"
          aria-label="Template menu"
        >
          <MoreVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => setIsPreview(true)}>Preview</DropdownMenuItem>
        {isEditMode && (
          <>
            <DropdownMenuItem onSelect={handleDuplicate}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem variant="destructive" onSelect={handleArchive}>
              Archive
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : undefined

  const editorBreadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/operations/checklisttemplates' })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to checklists"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/operations" className="hover:underline">
          Operations
        </Link>
        <span className="text-content-primary-subtle"> / </span>
        <Link to="/operations/checklisttemplates" className="hover:underline">
          Checklists
        </Link>
        <span className="text-content-primary-subtle"> / {topBarTitle}</span>
      </span>
    </span>
  )

  if (isEditMode && isLoading) {
    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title={topBarTitle} breadcrumb={editorBreadcrumb} />
          <FormSkeleton />
        </DesktopPage>
      )
    }
    return (
      <AppLayout
        topBar={<TopBarBackButton title={isPreview ? 'Preview' : 'Edit Checklist'} />}
        className="pt-6"
      >
        <FormSkeleton />
      </AppLayout>
    )
  }

  if (isPreview) {
    const previewItems = watch('items') ?? []
    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader
            title={topBarTitle}
            breadcrumb={
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsPreview(false)}
                  className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
                  aria-label="Back to editor"
                >
                  <ArrowLeft className="size-4" />
                </button>
                <span>
                  <Link to="/operations" className="hover:underline">
                    Operations
                  </Link>
                  <span className="text-content-primary-subtle"> / </span>
                  <Link to="/operations/checklisttemplates" className="hover:underline">
                    Checklists
                  </Link>
                  <span className="text-content-primary-subtle"> / {topBarTitle}</span>
                </span>
              </span>
            }
          />
          <ChecklistTemplatePreviewFields items={previewItems} />
        </DesktopPage>
      )
    }
    return (
      <AppLayout
        topBar={
          <TopBarBackButton title={topBarTitle} onBack={() => setIsPreview(false)} />
        }
        className="pt-6"
      >
        <ChecklistTemplatePreviewFields items={previewItems} />
      </AppLayout>
    )
  }

  // Map useFieldArray fields to FieldItem shape for the card component
  const fieldItems: FieldItem[] = fields.map((f, i) => {
    const watched = watch(`items.${i}`)
    return {
      id: f.id,
      type: watched?.type ?? 'Text',
      label: watched?.label ?? '',
      isRequired: watched?.isRequired ?? false,
      sortOrder: i,
    }
  })

  const editorBody = (
    <>
      <form
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className="flex flex-col gap-6 pb-24"
      >
        {/* Template Info Card */}
        <Card className="flex flex-col p-4">
          <TextRow
            control={control}
            name="name"
            label="Template Title"
            placeholder="Enter template name"
            nullable={false}
            error={errors.name?.message}
          />
          <TextareaField
            control={control}
            name="description"
            label="Description"
            placeholder="Optional description"
          />
        </Card>

        {/* Fields Section */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-base font-semibold tracking-[-0.5px]">Fields</p>
            <div className="flex items-center gap-3">
              {fields.length > 0 && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsArranging((v) => !v)}
                >
                  {isArranging ? 'Done' : 'Arrange'}
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={addField}
              >
                Add
              </Button>
            </div>
          </div>

          {fields.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title="No fields yet"
              description='Tap "Add Field" to start.'
              addButtonText="Add Field"
              onAdd={addField}
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={fieldItems.map((f) => f.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-3">
                  {fieldItems.map((field) => (
                    <ChecklistTemplateItemCard
                      key={field.id}
                      field={field}
                      isArranging={isArranging}
                      onUpdate={updateField}
                      onRemove={removeField}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Info Banner */}
        <Card className="bg-background-info-subtle border-info px-4 py-4">
          <p className="text-xs font-semibold text-content-info-bold">
            Photo upload is automatically included at the bottom of every
            checklist.
          </p>
        </Card>

        {/* Footer Buttons */}
        <div className="fixed bottom-0 left-0 right-0 bg-surface-L0 border-t border-border-L2 p-4 safe-bottom">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSaving || (!isDirty && !isFromStarter)}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </form>

      {/* Discard Changes Dialog */}
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave without
              saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDiscardDialog(false)}
            >
              Keep Editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => navigate({ to: '/operations/checklisttemplates' })}
            >
              Discard
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title={topBarTitle}
          breadcrumb={editorBreadcrumb}
          action={topBarRightIcon}
        />
        {editorBody}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title={topBarTitle} rightElement={topBarRightIcon} />}
      className="pt-6"
    >
      {editorBody}
    </AppLayout>
  )
}
